import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getPhotoUrl } from '../utils/photo';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

/*  tweddi — Chat (/chat/:matchId)
    tweddi identity: cream ground, Inclusive Sans, ink message bubbles, warm
    stone received bubbles. Logic (WebSocket, send, optimistic update) 100%
    preserved.                                                               */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL.replace(/^https/, 'wss').replace(/^http/, 'ws');
const CREAM  = '#FFFDF8';
const WASH   = '#FBF6EF';
const INK    = '#141313';
const MUTE   = '#6E6B62';
const BUBBLE = '#ECE7DF';   // warm stone for received messages
const RULE   = 'rgba(20,19,19,0.10)';

export default function ChatPage() {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const { user, token } = useAuth();
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [matchInfo, setMatchInfo] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => { fetchMessages(); fetchMatchInfo(); }, [matchId]);
  useEffect(() => {
    if (!token || !matchId) return;
    const connectWs = () => {
      const ws = new WebSocket(`${WS_URL}/ws/chat/${matchId}?token=${token}`);
      ws.onopen = () => { fetchMessages(); };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'ping') { ws.send(JSON.stringify({ type: 'pong' })); return; }
          if (data.type === 'new_message' && data.message) { setMessages(prev => { if (prev.some(m => m.id === data.message.id)) return prev; if (data.message.sender_id === user?.id) return prev; return [...prev, data.message]; }); }
        } catch {}
      };
      ws.onclose = (e) => { if (e.code === 4001 || e.code === 4003) return; setTimeout(connectWs, 3000); };
      ws.onerror = () => ws.close();
      wsRef.current = ws;
    };
    connectWs();
    return () => { if (wsRef.current) { wsRef.current.close(); wsRef.current = null; } };
  }, [token, matchId, user?.id]);
  useEffect(() => { if (messages.length > 0) messagesEndRef.current?.scrollIntoView({ behavior: 'instant' }); }, [messages.length]);

  const fetchMessages = async () => { try { const r = await axios.get(`${API}/chat/${matchId}`); setMessages(r.data.messages); } catch {} finally { setIsLoading(false); } };
  const fetchMatchInfo = async () => { try { const r = await axios.get(`${API}/matches`); const m = r.data.matches.find(m => m.match_id === matchId); if (m) setMatchInfo(m); } catch {} };

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || isSending) return;
    const content = newMessage.trim(); setNewMessage(''); setIsSending(true);
    const opt = { id: `temp-${Date.now()}`, sender_id: user.id, content, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, opt]);
    try { const r = await axios.post(`${API}/chat/send`, { match_id: matchId, content }); setMessages(prev => prev.map(m => m.id === opt.id ? r.data.message : m)); }
    catch { setMessages(prev => prev.filter(m => m.id !== opt.id)); toast.error('Failed to send message'); }
    finally { setIsSending(false); }
  };
  const formatTime = (d) => { try { return format(new Date(d), 'h:mm a'); } catch { return ''; } };

  return (
    <div style={{ background: `linear-gradient(180deg, ${CREAM} 70%, ${WASH} 100%)`, minHeight: '100dvh', maxWidth: '30rem', margin: '0 auto', display: 'flex', flexDirection: 'column' }} data-testid="chat-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inclusive+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap');
        * { box-sizing: border-box; }
        .ch-input:focus { outline: none; }
        .ch-input::placeholder { color: rgba(20,19,19,0.35); font-weight: 500; }
        .ch-back:hover { color: ${INK} !important; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }
        .ch-msg { animation: ch-pop .3s cubic-bezier(.16,1,.3,1); }
        @keyframes ch-pop { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) { .ch-msg { animation: none; } }
      `}</style>

      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 20, background: 'rgba(255,253,248,0.92)', backdropFilter: 'blur(16px)', borderBottom: `1px solid ${RULE}`, padding: '0.9rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button onClick={() => navigate('/matches')} data-testid="back-btn" className="ch-back" style={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTE, padding: 0, display: 'flex', alignItems: 'center', transition: 'color 0.2s', flexShrink: 0 }}>
          <ArrowLeft size={18} strokeWidth={1.75} />
        </button>
        <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', overflow: 'hidden', background: BUBBLE, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTE }}>
          {matchInfo?.matched_user?.photo_url ? <img src={getPhotoUrl(matchInfo.matched_user.photo_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <User size={15} strokeWidth={1.25} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.05rem', fontWeight: 600, color: INK, margin: 0, letterSpacing: '-0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{matchInfo?.matched_user?.name || 'Chat'}</p>
          <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.6rem', fontWeight: 600, color: MUTE, margin: 0, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{matchInfo?.matched_user?.relationship_to_couple || 'Wedding Guest'}</p>
        </div>
      </header>

      {/* Messages */}
      <main style={{ flex: 1, padding: '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <div className="spin" style={{ width: '1.1rem', height: '1.1rem', border: `1.5px solid ${RULE}`, borderTopColor: INK, borderRadius: '50%' }} />
          </div>
        ) : messages.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '3rem 1rem' }}>
            <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.7rem', fontStyle: 'italic', fontWeight: 500, color: INK, marginBottom: '0.4rem' }}>Say hello.</p>
            <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.72rem', fontWeight: 500, color: MUTE }}>Begin the conversation with {matchInfo?.matched_user?.name}</p>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => {
              const sent = msg.sender_id === user?.id;
              return (
                <div key={msg.id || i} className="ch-msg" style={{ display: 'flex', justifyContent: sent ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: '72%', padding: '0.8rem 1.05rem', background: sent ? INK : BUBBLE, borderRadius: sent ? '1.25rem 1.25rem 0.35rem 1.25rem' : '1.25rem 1.25rem 1.25rem 0.35rem' }}>
                    <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.86rem', fontWeight: 500, color: sent ? CREAM : INK, lineHeight: 1.5, margin: '0 0 0.2rem', letterSpacing: '0' }}>{msg.content}</p>
                    <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.56rem', fontWeight: 500, color: sent ? 'rgba(255,253,248,0.5)' : MUTE, margin: 0, letterSpacing: '0.04em' }}>{formatTime(msg.created_at)}</p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </main>

      {/* Input */}
      <footer style={{ position: 'sticky', bottom: 0, background: 'rgba(255,253,248,0.92)', backdropFilter: 'blur(16px)', borderTop: `1px solid ${RULE}`, padding: '0.9rem 1.5rem 1.25rem' }}>
        <form onSubmit={handleSend} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: CREAM, border: `1.5px solid ${INK}`, borderRadius: '9999px', padding: '0.4rem 0.4rem 0.4rem 1.25rem' }}>
          <input className="ch-input" value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Write a message…" data-testid="message-input" style={{ flex: 1, border: 'none', background: 'transparent', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, color: INK, padding: '0.5rem 0', outline: 'none', letterSpacing: '0' }} />
          <button type="submit" disabled={!newMessage.trim() || isSending} data-testid="send-btn" style={{ width: '2.6rem', height: '2.6rem', borderRadius: '50%', background: newMessage.trim() ? INK : 'rgba(20,19,19,0.15)', border: 'none', cursor: newMessage.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.2s', opacity: isSending ? 0.5 : 1 }}>
            {isSending ? <div className="spin" style={{ width: '1rem', height: '1rem', border: `1.5px solid rgba(255,253,248,0.3)`, borderTopColor: CREAM, borderRadius: '50%' }} /> : <Send size={16} strokeWidth={2} color={CREAM} />}
          </button>
        </form>
      </footer>
    </div>
  );
}
